import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Auth0CreateSession, Auth0ExchangeCode } from "@/redux/actions/session-action-types";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowToRight, faSearch } from "@fortawesome/pro-light-svg-icons";

export const AccountSelect = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const hasExchangedRef = useRef(false);

  const accounts = useSelector((state) => state.session?.auth0Accounts);
  const exchangeLoading = useSelector((state) => state.session?.auth0ExchangeLoading);
  const createSessionLoading = useSelector((state) => state.session?.auth0CreateSessionLoading);
  const submittingAccountId = useSelector((state) => state.session?.auth0SubmittingAccountId || "");
  const error = useSelector((state) => state.session?.auth0ExchangeError || "");

  const [accountList, setAccountList] = useState(accounts);

  const code = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("code") || "";
  }, [location.search]);

  const redirectUri = process.env.REACT_APP_AUTH0_CALLBACK_URL;

  useEffect(() => {
    if (hasExchangedRef.current) {
      return;
    }
    hasExchangedRef.current = true;

    dispatch(
      Auth0ExchangeCode({
        code,
        redirectUri,
      })
    );
  }, [code, dispatch, redirectUri]);

  useEffect(() => {
    // If there is only one account, select it
    if (accounts && accounts?.length === 1) {
      dispatch(
        Auth0CreateSession({
          accountId: accounts[0].id,
        })
      );
    } else {
      setAccountList(accounts);
    }
  }, [accounts]);

  const handleSelectAccount = (accountId) => {
    dispatch(
      Auth0CreateSession({
        accountId,
      })
    );
  };

  const onSearch = (e) => {
    if (e.target.value) {
      const list = accounts.filter((account) => account.company.toLowerCase().includes(e.target.value.toLowerCase()));
      setAccountList(list);
    } else {
      setAccountList(accounts);
    }
  };

  const loading = exchangeLoading || (createSessionLoading && accounts.length <= 1);

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 min-vw-100">
        <div className="d-flex flex-grow-1 justify-content-center align-items-center">
          <div className="text-center">
            <div className="py-4">
              <img src="/img/dragonflyai-ident.svg" height={48} alt="Dragonfly AI Logo" />
              <p className="lead">Please wait ...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="has-navbar-fixed d-flex align-items-center justify-content-center h-100">
        <main role="main" className="container">
          <Form className="form form-auth needs-validation m-auto" style={{ maxWidth: "420px" }}>
            <div className="m-auto">
              {/* Header */}
              <header className="text-center mb-4">
                <img src="/img/dragonflyai-ident.svg" height={48} alt="Dragonfly AI Ident" />
                <p className="lead">Choose an account</p>
              </header>
              {/* Messages */}
              {error ? <div className="alert alert-danger">{error}</div> : null}
              {/* List */}
              <div className="list-group" style={{ maxHeight: "calc(100vh - 250px)", overflowY: "auto" }}>
                <div className="input-group mb-4">
                  <div className="input-group-prepend d-flex">
                    <span className="input-group-text bg-white" id="basic-addon1">
                      <FontAwesomeIcon icon={faSearch} fixedWidth />
                    </span>
                  </div>
                  <input type="search" className="form-control" placeholder="Search..." onChange={(e) => onSearch(e)} />
                </div>
                {accountList && accounts?.length > 0 ? (
                  accountList.map((account, index) => {
                    return (
                      <a
                        href="#!"
                        key={index}
                        onClick={() => handleSelectAccount(account.id)}
                        className="list-group-item list-group-item-action p-3"
                        disabled={!!submittingAccountId}
                      >
                        <span>{account.company}</span>
                        <span className="text-black-50" style={{ float: "right" }}>
                          <FontAwesomeIcon size="lg" icon={faArrowToRight} />
                        </span>
                      </a>
                    );
                  })
                ) : (
                  <span className="text-primary">Company name not found.</span>
                )}
              </div>
              {/* Footer */}
              <footer className="text-center" />
            </div>
          </Form>
        </main>
      </div>
    </div>
  );
};
